import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CONVERSATION_CONFIG_TOKEN, TEMPLATES_PAGE_SIZE, ViewModeService } from '../../../../core/src';
import { TemplateService } from '../../../../core/src/lib/template.service';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MessageTemplate, PlatformLocation } from '@vendasta/conversation';
import { GalaxyColumnDef, GalaxyTableModule } from '@vendasta/galaxy/table';
import { Observable, map, shareReplay, of, combineLatest, catchError, BehaviorSubject, first } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { Scrollable } from '@vendasta/rx-utils';
import { GalaxyInfiniteScrollTriggerModule } from '@vendasta/galaxy/infinite-scroll-trigger';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { InboxService } from '../../../../core/src';

@Component({
  selector: 'inbox-list-message-templates',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    GalaxyPageModule,
    TranslateModule,
    MatIconModule,
    MatButtonModule,
    RouterLink,
    MatListModule,
    MatMenuModule,
    MatSortModule,
    MatTableModule,
    GalaxyTableModule,
    MatTooltipModule,
    GalaxyPipesModule,
    GalaxyInfiniteScrollTriggerModule,
    GalaxyLoadingSpinnerModule,
    GalaxyEmptyStateModule,
  ],
  templateUrl: './list-message-templates.component.html',
  styleUrls: ['./list-message-templates.component.scss'],
})
export class ListMessageTemplatesComponent {
  private readonly templateService = inject(TemplateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly inboxConfig = inject(CONVERSATION_CONFIG_TOKEN);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly viewModeService = inject(ViewModeService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly translateService = inject(TranslateService);
  private readonly analyticsService = inject(ProductAnalyticsService);
  private readonly inboxService = inject(InboxService);

  private scrollable: Scrollable<MessageTemplate, null> = this.getScrollableList();
  protected templates$ = this.scrollable.items$;

  private loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  readonly loading$ = this.loading$$.asObservable();

  readonly columns: GalaxyColumnDef[] = COLUMNS;
  readonly isBusinessApp = this.inboxConfig.platformLocation === PlatformLocation.PLATFORM_LOCATION_BUSINESS_APP;
  readonly viewMode$ = this.viewModeService.viewMode$;
  readonly isSidebar$ = this.viewMode$.pipe(map((mode) => mode === 'sidebar'));

  readonly mobile$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map((results) => results.matches),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly showFullButton$ = combineLatest([this.mobile$, this.isSidebar$]).pipe(
    map(
      ([isMobile, isSidebar]) => !isMobile && (!isSidebar || this.isBusinessApp),
      shareReplay({ refCount: true, bufferSize: 1 }),
    ),
  );

  back(): void {
    if (this.isBusinessApp) {
      this.router.navigate(['../inbox'], {
        relativeTo: this.route,
      });
    } else {
      this.router.navigate(['', { outlets: { inbox: 'inbox/settings' } }], {
        queryParamsHandling: 'merge',
      });
    }
  }

  loadNext(): void {
    this.scrollable.loadMore();
  }

  private getScrollableList() {
    return new Scrollable<MessageTemplate, null>(of(null), (_, cursor) => {
      return this.loadMore(cursor);
    });
  }

  loadMore(cursor: string) {
    return this.templateService.listTemplates(cursor, TEMPLATES_PAGE_SIZE).pipe(
      map((response) => {
        this.loading$$.next(false);
        return {
          items: response.templates,
          cursor: response.pagingMetadata?.nextCursor ?? '',
          hasMore: response.pagingMetadata.hasMore !== null,
        };
      }),
      catchError(() => of({ items: [] as Array<MessageTemplate>, cursor: '', hasMore: false })),
    );
  }

  async delete(templateId: string): Promise<void> {
    try {
      this.templateService.delete(templateId);
    } catch (e) {
      console.error('Error deleting template', templateId, e);
    } finally {
      this.templates$ = this.templates$.pipe(
        map((templates) => templates.filter((template) => template.templateId !== templateId)),
      );
    }
  }

  openDeleteConfirmationModal(templateId: string) {
    this.confirmationModal
      .openModal({
        type: 'warn',
        title: this.translateService.instant('INBOX.SETTINGS.MESSAGE_TEMPLATES.DELETE_TEMPLATE_MODAL.TITLE'),
        message: this.translateService.instant('INBOX.SETTINGS.MESSAGE_TEMPLATES.DELETE_TEMPLATE_MODAL.DESCRIPTION'),
        confirmButtonText: this.translateService.instant('INBOX.DELETE'),
        cancelButtonText: this.translateService.instant('INBOX.CANCEL'),
        width: 540,
      })
      .pipe(first())
      .subscribe((userDidAction) => {
        if (userDidAction) {
          this.delete(templateId);
        }
      });
  }

  async goToEditTemplate(template: MessageTemplate): Promise<void> {
    this.router.navigate([`./${encodeURIComponent(template.templateId)}`], {
      relativeTo: this.route,
      state: template,
    });
    const trackingProps = await this.inboxService.buildTemplateTrackProperties();
    this.analyticsService.trackEvent('inbox', 'template', 'edit-template-click', 0, {
      trackingProps,
      templateId: template.templateId,
    });
  }

  isDefaultTemplate(templateId: string): boolean {
    return templateId.includes('default');
  }

  async redirectToCreateTemplate(): Promise<void> {
    this.router.navigate(['./create'], { relativeTo: this.route });
    const trackingProps = await this.inboxService.buildTemplateTrackProperties();
    this.analyticsService.trackEvent('inbox', 'template', 'create-template-click', 0, trackingProps);
  }
}

export const COLUMNS: GalaxyColumnDef[] = [
  {
    id: 'template_name',
    sortable: false,
  },
  {
    id: 'template_content',
    sortable: false,
  },
  {
    id: 'created_date',
    sortable: false,
  },
  {
    id: 'actions',
    pinned: true,
    sortable: false,
    stickyEnd: true,
  },
];
